import { EMPLOYEE_RECORD_LABELS } from "@/lib/employeeRecordLabels";
import { api } from "@/lib/api";
import type { CurrentAssignment } from "@/lib/workerOverview";
import type { StatusDozvole, Uskladenost, ZdravstveniStatus } from "@/lib/status";

export const RAZLOG_PRESTANKA = {
  sporazum: "Sporazumni prestanak",
  otkaz_poslodavac: "Otkaz poslodavca",
  otkaz_radnik: "Otkaz radnika",
  istek_ugovora: "Istek ugovora",
  umirovljenje: "Umirovljenje",
  smrt: "Smrt",
  ostalo: "Ostalo",
} as const;

/**
 * The worker resource, as the API returns it.
 *
 * **Every field is optional, and that is the contract, not laziness.** The
 * server builds each payload from the fields the caller is allowed to see
 * (§7), so a site manager's worker genuinely has no `bruto_placa` key — not
 * `null`, absent. Screens must render whatever arrives without holes, and must
 * never assume a field is there because it was there for somebody else.
 */
export interface Radnik {
  id?: string;
  ime?: string;
  prezime?: string;
  radno_mjesto?: string | null;
  aktivan?: boolean;
  izvor?: "avm" | "minimax" | "povijesni";
  status_oznaka?: string;
  broj_putovnice?: string | null;

  oib?: string | null;
  spol?: string;
  datum_rodenja?: string;
  national_identification_number?: string | null;
  drzavljanstvo_iso2?: string | null;
  postal_code?: string | null;
  postal_name?: string | null;
  residence_country_code?: string | null;
  tax_residency_country_code?: string | null;
  datum_pocetka_rada?: string;
  radno_mjesto_id?: string | null;
  vrsta_ugovora?: string;
  ugovor_vrijedi_do?: string | null;
  datum_prestanka?: string | null;
  razlog_prestanka?: string | null;
  datum_prijave_osiguranja?: string | null;
  datum_staza_kod_poslodavca?: string;
  hitni_kontakt_ime?: string | null;
  hitni_kontakt_telefon?: string | null;
  email?: string | null;
  mobile_phone?: string | null;
  messaging_consent?: boolean | null;
  health_insurance_number?: string | null;
  pension_insurance_number?: string | null;
  health_card_number?: string | null;
  note?: string | null;
  in_use?: boolean | null;
  povecani_staz?: boolean;
  spor_u_tijeku?: boolean;
  fotografija_dokument_id?: string | null;
  fotografija_placeholder?: boolean;

  tarifna_grupa?: string | null;
  bruto_placa?: string | null;
  iban?: string | null;
  adresa_prebivaliste?: string | null;
  adresa_boraviste?: string | null;

  zdravstveni_status?: ZdravstveniStatus | null;
  dozvola_status?: StatusDozvole | null;
  uskladenost?: Uskladenost | null;
  oprema_status?: StatusOpreme | null;
  blokada_override_aktivan?: boolean;
  minimax_identity_warning?: string | null;
  trenutni_raspored?: CurrentAssignment | null;
}

export interface Stranica<T> {
  total: number;
  items: T[];
}

export interface UpitRadnika {
  gradiliste_id?: string;
  status?: "aktivni" | "bivsi" | "povijesni" | "svi";
  q?: string;
  limit?: number;
  offset?: number;
  samo_aktivni?: boolean;
}

export interface HistoricalWorkerInput {
  ime: string;
  prezime: string;
  oib: string | null;
  datum_rodenja: string | null;
  broj_putovnice: string | null;
  datum_prestanka: string | null;
}
export async function createHistoricalWorker(body: HistoricalWorkerInput): Promise<Radnik> {
  return (await api.post<Radnik>("/radnici/povijesni", body)).data;
}
export function statusRadnika(worker: Radnik): string {
  return worker.status_oznaka ?? (worker.izvor === "povijesni" ? "povijesni" : worker.aktivan === true ? "aktivan" : worker.aktivan === false ? `bivši${worker.datum_prestanka ? ` ${worker.datum_prestanka.slice(0, 4)}` : ""}` : "");
}
export function workerChoiceLabel(worker: Radnik): string {
  const status = statusRadnika(worker);
  return `${punoIme(worker)}${status ? ` · ${status}` : ""}`;
}

export async function dohvatiRadnike(
  upit: UpitRadnika,
  signal?: AbortSignal,
): Promise<Stranica<Radnik>> {
  const response = await api.get<Stranica<Radnik>>("/radnici", {
    params: upit,
    signal,
  });
  return response.data;
}

export async function dohvatiRadnika(
  id: string,
  signal?: AbortSignal,
): Promise<Radnik> {
  const response = await api.get<Radnik>(`/radnici/${id}`, { signal });
  return response.data;
}

export interface PayrollProfil {
  employment_terms: {
    id?: string;
    contract_start_on?: string | null;
    contract_end_on?: string | null;
    employment_type: string;
    contract_type: string;
    contracted_gross_salary: string | null;
    gross_salary_source: string | null;
    gross_salary_synchronized_at: string | null;
    weekly_hours: string | null;
    weekly_working_days: number | null;
    annual_hours_fund: number | null;
    collective_agreement_applicable: boolean | null;
    net_hourly_rate: string | null;
    employment_percentage: string | null;
    other_employer_percentage: string | null;
    meal_benefit_mode: "employer_provided" | "cash_payment" | "none" | null;
    meal_allowance: string | null;
    daily_meal_price: string | null;
    transport_base_type: string | null;
    transport_allowance: string | null;
    automatic_payments: boolean | null;
    holiday_allowance_amount: string | null;
    seniority_start_date: string | null;
    full_month_insurance: boolean | null;
    pension_pillar_one: boolean | null;
    pension_pillar_two: boolean | null;
    health_contribution_exempt: boolean | null;
    workplace_municipality_code: string | null;
    insurance_base_amount: string | null;
    insurance_base_reduction_applied: boolean | null;
    insurance_base_reduction_percentage: string | null;
    higher_insurance_base_calculation: boolean | null;
    higher_insurance_base_amount: string | null;
    contributions_deducted_from_bonus: boolean | null;
    foreign_health_contribution: boolean | null;
    prior_experience_years: number | null;
    prior_experience_months: number | null;
    prior_experience_days: number | null;
    prior_organization_experience_years: number | null;
    prior_organization_experience_months: number | null;
    prior_organization_experience_days: number | null;
    subsidy_type: string | null;
    subsidy_reference_number: string | null;
    subsidy_from: string | null;
    subsidy_to: string | null;
    disability_allowance: boolean | null;
    disability_above_quota: boolean | null;
    disability_case_number: string | null;
    disability_percentage: string | null;
    disability_amount: string | null;
  } | null;
  accounts: {
    iban: string;
    bic: string | null;
    account_type: string;
    allocation_percentage: string | null;
    allocation_amount: string | null;
    is_protected_account: boolean;
  }[];
  deductions: { name: string; value: string; calculation_method: string }[];
  dependents: { full_name: string; relationship: string }[];
  tax_card: {
    tax_card_number: string | null;
    general_allowance_claimed: boolean | null;
    personal_allowance: string | null;
    allowance_coefficient: string | null;
    disability_personal_allowance: boolean | null;
    main_employer: boolean | null;
    holiday_income_tax_percentage: string | null;
    tax_treaty_exemption: boolean | null;
    tax_treaty_percentage: string | null;
  } | null;
  tax_snapshots: unknown[];
}

export interface PayrollPostavkeUpdate {
  net_hourly_rate: string;
  employment_percentage: string;
  meal_benefit_mode: "employer_provided" | "cash_payment" | "none";
  meal_allowance: string | null;
  seniority_start_date: string;
  full_month_insurance: boolean;
  pension_pillar_one: boolean;
  pension_pillar_two: boolean;
  health_contribution_exempt: boolean;
}

export async function dohvatiPayrollProfil(id: string, signal?: AbortSignal): Promise<PayrollProfil> {
  const response = await api.get<PayrollProfil>(`/radnici/${id}/payroll-profile`, { signal });
  return response.data;
}

export async function azurirajPayrollPostavke(
  id: string,
  podaci: PayrollPostavkeUpdate,
): Promise<PayrollProfil> {
  const response = await api.patch<PayrollProfil>(
    `/radnici/${id}/payroll-profile/settings`,
    podaci,
  );
  return response.data;
}

export interface HtzVelicineInput {
  gornji_dio: string | null;
  donji_dio: string | null;
  obuca: string | null;
  kaciga: string | null;
  rukavice: string | null;
  zastitni_pojas: string | null;
  prsluk: string | null;
}

export interface HtzVelicine extends HtzVelicineInput {
  id: string;
  radnik_id: string;
  created_at: string;
  updated_at: string;
}

export interface VrstaPosebnePotrebe {
  id: string;
  code: string;
  naziv: string;
  vrijednost_oznaka: string | null;
  aktivna: boolean;
}

export interface PosebnaPotrebaInput {
  vrsta_id: string;
  opis: string | null;
  vrijednost: string | null;
  napomena: string | null;
  aktivna: boolean;
}

export interface PosebnaPotreba extends PosebnaPotrebaInput {
  id: string;
  radnik_id: string;
  created_at: string;
  updated_at: string;
}

export interface VozackaDozvolaInput {
  broj_dozvole: string | null;
  kategorije: string[];
  datum_izdavanja: string | null;
  datum_isteka: string | null;
  zemlja_izdavanja_iso2: string | null;
  verificirana_u_rh: boolean | null;
  datum_verifikacije: string | null;
  napomena: string | null;
  dokument_id?: string | null;
}

export interface VozackaDozvola extends VozackaDozvolaInput {
  kategorije_detalji?: { kategorija: string; issued_on: string | null; expires_on: string | null }[];
  id: string;
  radnik_id: string;
  employee_document_id: string;
  created_at: string;
  updated_at: string;
}

export type VrstaIdentifikacijskogDokumenta =
  | "passport_identity_page"
  | "identity_card"
  | "single_residence_work_permit"
  | "other_identity_document";

export interface IdentifikacijskiDokumentInput {
  vrsta: VrstaIdentifikacijskogDokumenta;
  broj_dokumenta: string | null;
  izdavatelj: string | null;
  zemlja_izdavanja_iso2: string | null;
  datum_izdavanja: string | null;
  datum_isteka: string | null;
  napomena: string | null;
  dokument_id?: string | null;
}

export interface IdentifikacijskiDokument
  extends IdentifikacijskiDokumentInput {
  id: string;
  radnik_id: string;
  employee_document_id: string;
  created_at: string;
  updated_at: string;
}

export async function dohvatiHtzVelicine(
  radnikId: string,
  signal?: AbortSignal,
): Promise<HtzVelicine> {
  const response = await api.get<HtzVelicine>(`/radnici/${radnikId}/htz-velicine`, {
    signal,
  });
  return response.data;
}

export async function spremiHtzVelicine(
  radnikId: string,
  podaci: HtzVelicineInput,
  postoje: boolean,
): Promise<HtzVelicine> {
  const response = postoje
    ? await api.patch<HtzVelicine>(`/radnici/${radnikId}/htz-velicine`, podaci)
    : await api.post<HtzVelicine>(`/radnici/${radnikId}/htz-velicine`, podaci);
  return response.data;
}

export async function dohvatiVrstePosebnihPotreba(
  radnikId: string,
  signal?: AbortSignal,
): Promise<VrstaPosebnePotrebe[]> {
  const response = await api.get<VrstaPosebnePotrebe[]>(
    `/radnici/${radnikId}/posebne-potrebe-oprema/vrste`,
    { signal },
  );
  return response.data;
}

export async function dohvatiPosebnePotrebe(
  radnikId: string,
  signal?: AbortSignal,
): Promise<PosebnaPotreba[]> {
  const response = await api.get<PosebnaPotreba[]>(
    `/radnici/${radnikId}/posebne-potrebe-oprema`,
    { signal },
  );
  return response.data;
}

export async function spremiPosebnuPotrebu(
  radnikId: string,
  podaci: PosebnaPotrebaInput,
  zapisId?: string,
): Promise<PosebnaPotreba> {
  const putanja = `/radnici/${radnikId}/posebne-potrebe-oprema`;
  const response = zapisId
    ? await api.patch<PosebnaPotreba>(`${putanja}/${zapisId}`, podaci)
    : await api.post<PosebnaPotreba>(putanja, podaci);
  return response.data;
}

export async function deaktivirajPosebnuPotrebu(
  radnikId: string,
  zapisId: string,
): Promise<PosebnaPotreba> {
  const response = await api.patch<PosebnaPotreba>(
    `/radnici/${radnikId}/posebne-potrebe-oprema/${zapisId}`,
    { aktivna: false },
  );
  return response.data;
}

export async function dohvatiVozackuDozvolu(
  radnikId: string,
  signal?: AbortSignal,
): Promise<VozackaDozvola> {
  const response = await api.get<VozackaDozvola>(
    `/radnici/${radnikId}/vozacka-dozvola`,
    { signal },
  );
  return response.data;
}

export async function spremiVozackuDozvolu(
  radnikId: string,
  podaci: VozackaDozvolaInput,
  postoji: boolean,
): Promise<VozackaDozvola> {
  const putanja = `/radnici/${radnikId}/vozacka-dozvola`;
  const response = postoji
    ? await api.patch<VozackaDozvola>(putanja, podaci)
    : await api.post<VozackaDozvola>(putanja, podaci);
  return response.data;
}

export async function dohvatiIdentifikacijskeDokumente(
  radnikId: string,
  signal?: AbortSignal,
): Promise<IdentifikacijskiDokument[]> {
  const response = await api.get<IdentifikacijskiDokument[]>(
    `/radnici/${radnikId}/identifikacijski-dokumenti`,
    { signal },
  );
  return response.data;
}

export async function spremiIdentifikacijskiDokument(
  radnikId: string,
  podaci: IdentifikacijskiDokumentInput,
  zapisId?: string,
): Promise<IdentifikacijskiDokument> {
  const putanja = `/radnici/${radnikId}/identifikacijski-dokumenti`;
  const response = zapisId
    ? await api.patch<IdentifikacijskiDokument>(`${putanja}/${zapisId}`, podaci)
    : await api.post<IdentifikacijskiDokument>(putanja, podaci);
  return response.data;
}

/** Full name in the order the list is sorted by: surname first. */
export function punoIme(radnik: Radnik): string {
  return [radnik.prezime, radnik.ime].filter(Boolean).join(" ");
}

/** Initials for the avatar circle on the worker card. */
export function inicijali(radnik: Radnik): string {
  return [radnik.ime, radnik.prezime]
    .filter(Boolean)
    .map((dio) => dio!.charAt(0).toUpperCase())
    .join("");
}

/** A permit, as the sub-resource returns it. */
export interface Dozvola {
  revoked_on?: string | null;
  id: string;
  radnik_id: string;
  tip: string;
  broj_dozvole?: string | null;
  izdavatelj?: string | null;
  zemlja_iso2?: string | null;
  datum_izdavanja: string;
  datum_isteka: string;
  datum_podnosenja_produljenja: string | null;
  dokument_id: string | null;
  napomena: string | null;
}

/** An RA-2 certificate: the verdict and the dates, and nothing else exists. */
export interface ZdravstvenaSposobnost {
  id: string;
  radnik_id: string;
  ocjena: string;
  vrsta_pregleda: string;
  datum_pregleda: string;
  datum_sljedeceg_pregleda: string | null;
  pur_posao_id: string | null;
  pur_posao_naziv?: string | null;
  dokument_id: string | null;
}

export interface Kvalifikacija {
  trajno?: boolean;
  id: string;
  radnik_id: string;
  tip: string;
  naziv: string;
  izdavatelj: string | null;
  datum_izdavanja: string | null;
  datum_isteka: string | null;
  dokument_id: string | null;
}

export interface Osposobljavanje {
  id: string;
  radnik_id: string;
  vrsta_id: string | null;
  vrsta_naziv?: string | null;
  vrsta_izvorni_tekst?: string | null;
  napomena?: string | null;
  status: string;
  dogadaj_okidac: string;
  nadzor_pocetak: string | null;
  datum_osposobljavanja: string | null;
  dokument_id: string | null;
}

export interface EquipmentReplacementReceipt {
  operation_id: string;
  undo_until: string;
}

export interface OzoZaduzenje {
  izdavanje_id?: string | null;
  razduzenje_id?: string | null;
  gradiliste_id?: string | null;
  pocetno_stanje?: boolean;
  razlog_izvan_kvote?: string | null;
  vratio_staro?: boolean | null;
  id: string;
  radnik_id: string;
  vrsta_ozo_id: string | null;
  vrsta_ozo_naziv?: string | null;
  vrsta_izvorni_tekst?: string | null;
  napomena?: string | null;
  kolicina?: number | null;
  velicina?: string | null;
  zaduzio_text?: string | null;
  datum_zaduzenja: string;
  rok_zamjene: string | null;
  datum_razduzenja: string | null;
  oznaka: string | null;
  potvrda_dokument_id: string | null;
}

async function podresurs<T>(
  radnikId: string,
  putanja: string,
  signal?: AbortSignal,
): Promise<T[]> {
  const response = await api.get<T[]>(`/radnici/${radnikId}/${putanja}`, {
    signal,
  });
  return response.data;
}

export const dohvatiDozvole = (id: string, signal?: AbortSignal) =>
  podresurs<Dozvola>(id, "dozvole", signal);

/**
 * Reading this records an entry in the special-category access log (GDPR
 * čl. 5/2). The screen says so before it is opened; nobody should discover
 * afterwards that their look was written down.
 */
export const dohvatiZdravstvene = (id: string, signal?: AbortSignal) =>
  podresurs<ZdravstvenaSposobnost>(id, "zdravstvene-sposobnosti", signal);

export const dohvatiKvalifikacije = (id: string, signal?: AbortSignal) =>
  podresurs<Kvalifikacija>(id, "kvalifikacije", signal);

export const dohvatiOsposobljavanja = (id: string, signal?: AbortSignal) =>
  podresurs<Osposobljavanje>(id, "osposobljavanja", signal);

export const dohvatiOzo = (id: string, signal?: AbortSignal) =>
  podresurs<OzoZaduzenje>(id, "zastitna-oprema", signal);

/** Human labels for the enum values the API returns. */
export const TIP_DOZVOLE: Record<string, string> = EMPLOYEE_RECORD_LABELS.dozvola!.tip!;

export const OCJENA: Record<string, string> = EMPLOYEE_RECORD_LABELS.zdravstvena_sposobnost!.ocjena!;

export const VRSTA_PREGLEDA: Record<string, string> = EMPLOYEE_RECORD_LABELS.zdravstvena_sposobnost!.vrsta_pregleda!;

export const VRSTA_UGOVORA: Record<string, string> = EMPLOYEE_RECORD_LABELS.employee_employment_terms!.contract_type!;

export const TIP_KVALIFIKACIJE: Record<string, string> = EMPLOYEE_RECORD_LABELS.kvalifikacija!.tip!;

export const STATUS_OSPOSOBLJAVANJA: Record<string, string> = EMPLOYEE_RECORD_LABELS.osposobljavanje_znr!.status!;

/** Equipment replacement summary, behind `znr:read`. */
export interface StatusOpreme {
  najraniji_rok: string | null;
  dana_do_roka: number | null;
  komada_zaduzeno: number;
  istekao: boolean;
}

/** One entry of the change feed: field names, never their values. */
export interface StavkaZapisnika {
  id: string;
  kada: string;
  radnja: string;
  entitet: string;
  entitet_id: string | null;
  polja: string[];
  tko: string | null;
  radnik: string | null;
}

export async function dohvatiZapisnik(
  limit = 8,
  signal?: AbortSignal,
): Promise<StavkaZapisnika[]> {
  const response = await api.get<StavkaZapisnika[]>("/zapisnik", {
    params: { limit },
    signal,
  });
  return response.data;
}

/** A post from the reference table, for the form's select. */
export interface RadnoMjesto {
  id: string;
  naziv: string;
  tarifna_grupa: string;
}

/** A country, for the citizenship select. */
export interface Drzava {
  iso2: string;
  naziv: string;
}

export async function spremiRadnika(podaci: Partial<Radnik> & { foreign_details?: Record<string, string | null> }): Promise<Radnik> {
  const response = await api.post<Radnik>("/radnici", podaci);
  return response.data;
}

export async function izmijeniRadnika(
  id: string,
  podaci: Partial<Radnik>,
): Promise<Radnik> {
  const response = await api.patch<Radnik>(`/radnici/${id}`, podaci);
  return response.data;
}

export async function dodajDozvolu(
  radnikId: string,
  podaci: Record<string, unknown>,
): Promise<Dozvola> {
  const response = await api.post<Dozvola>(
    `/radnici/${radnikId}/dozvole`,
    podaci,
  );
  return response.data;
}

export async function dodajKvalifikaciju(
  radnikId: string,
  podaci: Record<string, unknown>,
): Promise<Kvalifikacija> {
  const response = await api.post<Kvalifikacija>(
    `/radnici/${radnikId}/kvalifikacije`,
    podaci,
  );
  return response.data;
}

/** One row of the import report, as the API returns it. */
export interface StavkaUvoza {
  redak: number;
  ime: string;
  prezime: string;
  oib: string;
  ishod: "uvozi_se" | "preskace_se" | "uvezen" | "preskocen";
  razlog: string | null;
}

export interface IzvjestajUvoza {
  redaka: number;
  za_uvoz: number;
  preskocenih: number;
  stavke: StavkaUvoza[];
  upozorenja: string[];
  nepoznata_radna_mjesta: string[];
  nepoznata_drzavljanstva: string[];
}

/** What to do with a post the reference table does not have. */
export interface OdlukaOMjestu {
  naziv: string;
  postojece_id?: string;
  nova_tarifna_grupa?: string;
}

export async function preuzmiPredlozak(): Promise<Blob> {
  const response = await api.get("/uvoz/predlozak", { responseType: "blob" });
  return response.data as Blob;
}

export async function analizirajDatoteku(
  datoteka: File,
): Promise<IzvjestajUvoza> {
  const tijelo = new FormData();
  tijelo.append("datoteka", datoteka);
  const response = await api.post<IzvjestajUvoza>("/uvoz/analiza", tijelo);
  return response.data;
}

export async function uveziDatoteku(
  datoteka: File,
  odluke: OdlukaOMjestu[],
): Promise<IzvjestajUvoza> {
  const tijelo = new FormData();
  tijelo.append("datoteka", datoteka);
  if (odluke.length > 0) tijelo.append("mapiranja", JSON.stringify(odluke));
  const response = await api.post<IzvjestajUvoza>("/uvoz/uvoz", tijelo);
  return response.data;
}

export async function preuzmiIzvjestaj(datoteka: File): Promise<Blob> {
  const tijelo = new FormData();
  tijelo.append("datoteka", datoteka);
  const response = await api.post("/uvoz/izvjestaj", tijelo, {
    responseType: "blob",
  });
  return response.data as Blob;
}

/** The thirteen wage grades (KUG čl. 39), for the new-post select. */
export const TARIFNE_GRUPE = [
  "I", "II", "III", "IV.a", "IV.b", "V.a", "V.b",
  "VI", "VII", "VIII", "IX", "X", "XI",
] as const;

/** Update only the filing date on an existing permit. */
export async function zabiljeziProduljenjeDozvole(radnikId: string, dozvolaId: string, datumPodnosenja: string): Promise<Dozvola> {
  return (await api.patch<Dozvola>(`/radnici/${radnikId}/dozvole/${dozvolaId}`, { datum_podnosenja_produljenja: datumPodnosenja })).data;
}
